import { useTheme } from '@emotion/react'
import {
    AddToCartEvent,
    AppState,
    AuthenticationState,
    BreachEvent,
    click_origin_breach_screen,
    DataLayerEventName,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { getAttribution } from '../../../../../errors/ErrorPage'
import { useAuthActions } from '../../../../authentication'
import { getSubscriptionValue } from '../../../common'
import { createLinkClickEvent } from '../../../createLinkClickHandler'
import {
    everydayDigitalClassName,
    StyledBreachContainer,
    StyledBreachFooter,
    StyledContactDetails,
    StyledDayRange,
    StyledHeading,
    StyledLogInButton,
    StyledLoginContainer,
    StyledOutlineAnchor,
    StyledParagraph,
    StyledParagraphAboveMobile,
    StyledParagraphMobileOnly,
    StyledPlusIcon,
    StyledPremiumLabel,
    StyledSubcriptionSubtitle,
    StyledSubcriptionText,
    StyledSubscribeButton,
    StyledSubscription,
    StyledSubscriptionContainer,
    StyledSubscriptionHeading,
    StyledSubscriptionInfo,
    StyledSubscriptionInfoContainer,
    StyledSubscriptionWrapper,
    StyledWAFootyLogo,
    ultimatePackClassName,
} from './SportBreachNotification.styled'

const plusIcon = require('./assets/plus.png')

export interface SportBreachNotificationProps {
    breachCreativeName: string
    breachDeal: string
    onEvent: (event: BreachEvent) => void
}

export function SportBreachNotification({
    breachDeal,
    breachCreativeName,
    onEvent,
}: SportBreachNotificationProps) {
    const { isLoggedIn } = useSelector<AppState, AuthenticationState>(
        (state) => state.authentication,
    )
    const { onSubscribeClick, onLoginClick } = useAuthActions()

    const theme = useTheme()
    const masthead =
        getAttribution(theme.kind, theme.section) || 'The West Australian'

    const packages: AddToCartEvent['payload'][] = [
        {
            currency: 'AUD',
            item_id: 'WST-M--TFSS+D7.50_DD',
            item_brand: masthead,
            item_name: 'Digital + Print',
            price: getSubscriptionValue(13),
            quantity: 1,
            coupon: 'FOOTYPAK',
            discount: getSubscriptionValue(5.5),
            item_variant: 'native',
        },
        {
            currency: 'AUD',
            item_id: 'WST_DIGI_$3.50X8W',
            item_brand: masthead,
            item_name: 'Everyday Digital',
            price: getSubscriptionValue(7),
            quantity: 1,
            coupon: 'DIG50',
            discount: getSubscriptionValue(3.5),
            item_variant: 'native',
        },
    ]

    useEffect(() => {
        const viewSubscriptionItems: ViewSubscriptionItem[] =
            packages.map((pkg, index) => ({
                index,
                item_name: pkg.item_name,
                item_id: pkg.item_id,
                item_brand: masthead,
                component_path: `offercheck/thewest?redirect=true&offerCode=${pkg.coupon}`,
                item_list_category: 'freemium',
                item_list_category2: 'Premium - Inline',
                item_list_name: breachCreativeName,
                price: getSubscriptionValue(pkg.price),
                quantity: 1,
                currency: 'AUD',
            })) || []

        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'breachscreen',
            payload: { items: viewSubscriptionItems },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <StyledBreachContainer data-testid="SportBreachScreen">
            <StyledHeading>Subscribe to the West Australian</StyledHeading>
            <StyledPremiumLabel>
                This article is available to subscribers who have digital access
                included in their subscription.
                {!isLoggedIn && (
                    <StyledLoginContainer>
                        {' '}
                        <StyledLogInButton onClick={onLoginClick}>
                            Log in
                        </StyledLogInButton>
                    </StyledLoginContainer>
                )}
            </StyledPremiumLabel>
            <StyledWAFootyLogo />
            <StyledSubcriptionSubtitle>
                <StyledSubcriptionText>
                    Don’t miss the latest footy news!
                </StyledSubcriptionText>
            </StyledSubcriptionSubtitle>
            <StyledSubscriptionContainer>
                <StyledSubscription className={ultimatePackClassName}>
                    <StyledSubscriptionWrapper>
                        <StyledSubscriptionHeading>
                            The Footy Pack
                        </StyledSubscriptionHeading>
                        <StyledSubscriptionInfoContainer>
                            <StyledSubscriptionInfo bold>
                                Digital + Print
                            </StyledSubscriptionInfo>
                        </StyledSubscriptionInfoContainer>
                        <StyledSubscriptionInfoContainer>
                            <StyledSubscriptionInfo strikethrough>
                                $13
                            </StyledSubscriptionInfo>{' '}
                            <StyledSubscriptionInfo bold>
                                $7.50
                            </StyledSubscriptionInfo>{' '}
                            <StyledSubscriptionInfo>
                                per week for 8 weeks*
                            </StyledSubscriptionInfo>
                        </StyledSubscriptionInfoContainer>
                        <StyledSubscribeButton
                            onClick={() => {
                                createLinkClickEvent(
                                    breachCreativeName,
                                    breachDeal,
                                    onEvent,
                                    undefined,
                                    1,
                                    packages[0],
                                )()
                                onSubscribeClick({
                                    offerCode: 'FOOTYPAK',
                                    callToAction: click_origin_breach_screen,
                                })
                            }}
                        >
                            Subscribe Now
                        </StyledSubscribeButton>
                        <StyledParagraph>
                            Enjoy unlimited access to thewest.com.au and
                            everyday digital editions on any device.
                        </StyledParagraph>
                        <StyledDayRange>
                            <StyledParagraph marginTop bold>
                                <StyledPlusIcon src={plusIcon} alt="" />
                                Thursday – Monday
                            </StyledParagraph>
                        </StyledDayRange>
                        <StyledParagraphAboveMobile bold>
                            Home delivery, with the latest footy news, new look
                            Pre-Game and The Game lift outs with fixtures,
                            highlights and expert opinions from WA’s leading
                            football writers.
                        </StyledParagraphAboveMobile>
                        <StyledParagraphMobileOnly bold>
                            Papers home delivered with all the latest footy
                            news!
                        </StyledParagraphMobileOnly>
                        <StyledParagraph marginTop>*T&Cs apply</StyledParagraph>
                    </StyledSubscriptionWrapper>
                </StyledSubscription>
                <StyledSubscription className={everydayDigitalClassName}>
                    <StyledSubscriptionWrapper>
                        <StyledSubscriptionHeading>
                            Everyday Digital
                        </StyledSubscriptionHeading>
                        <StyledSubscriptionInfoContainer>
                            <StyledSubscriptionInfo bold>
                                Digital only
                            </StyledSubscriptionInfo>
                        </StyledSubscriptionInfoContainer>
                        <StyledSubscriptionInfoContainer>
                            <StyledSubscriptionInfo bold>
                                $3.50
                            </StyledSubscriptionInfo>{' '}
                            <StyledSubscriptionInfo>
                                per week for 8 weeks*
                            </StyledSubscriptionInfo>
                        </StyledSubscriptionInfoContainer>
                        <StyledSubscribeButton
                            onClick={() => {
                                createLinkClickEvent(
                                    breachCreativeName,
                                    breachDeal,
                                    onEvent,
                                    undefined,
                                    1,
                                    packages[1],
                                )()
                                onSubscribeClick({
                                    offerCode: 'DIG50',
                                    callToAction: click_origin_breach_screen,
                                })
                            }}
                        >
                            Subscribe Now
                        </StyledSubscribeButton>
                        <StyledParagraph>
                            Enjoy unlimited access to thewest.com.au PLUS daily
                            digital editions of The West Australian and The
                            Sunday Times on any device!
                        </StyledParagraph>
                    </StyledSubscriptionWrapper>
                </StyledSubscription>
            </StyledSubscriptionContainer>
            <StyledBreachFooter>
                {/* <StyledBreachFooterText></StyledBreachFooterText> */}
                <StyledContactDetails>
                    Need Help? Call <a href="tel:1800 811 855">1800 811 855</a>{' '}
                    or view{' '}
                    <StyledOutlineAnchor
                        onClick={() => {
                            createLinkClickEvent(
                                breachCreativeName,
                                breachDeal,
                                onEvent,
                            )()
                            onSubscribeClick({
                                callToAction: click_origin_breach_screen,
                            })
                        }}
                        tabIndex={0}
                    >
                        all subscription packages
                    </StyledOutlineAnchor>
                </StyledContactDetails>
            </StyledBreachFooter>
        </StyledBreachContainer>
    )
}
